/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * DAS Tree Sidebar
 * Collapsible tree view that syncs with the topology canvas
 */

import React from "react";
import {
  Box,
  List,
  ListItemButton,
  Collapse,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { ExpandLess, ExpandMore, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { TopologyNode } from "./types";
import { STATUS_COLORS, NODE_TYPE_CONFIG } from "./constants";

interface DasTreeSidebarProps {
  root: TopologyNode | null;
  collapsedNodeIds: Set<string>;
  selectedNodeId: string | null;
  isCollapsed: boolean;
  statusFilter: string;
  typeFilter: string;
  hcmLedColor: string;
  visibleNodeIdsForInventoryFilter: Set<string> | null;
  onNodeClick: (nodeId: string) => void;
  onNodeToggle: (nodeId: string) => void;
  onSidebarToggle: () => void;
}

interface TreeItemProps {
  node: TopologyNode;
  level: number;
  collapsedNodeIds: Set<string>;
  selectedNodeId: string | null;
  statusFilter: string;
  typeFilter: string;
  hcmLedColor: string;
  visibleNodeIdsForInventoryFilter: Set<string> | null;
  onNodeClick: (nodeId: string) => void;
  onNodeToggle: (nodeId: string) => void;
}

// Helper function to check if node or any descendants match filters
const nodeOrDescendantsMatchFilters = (
  node: TopologyNode,
  statusFilter: string,
  typeFilter: string,
  visibleNodeIdsForInventoryFilter: Set<string> | null
): boolean => {
  // If inventory filter is active, only check if node is in visible set
  if (visibleNodeIdsForInventoryFilter !== null) {
    // Check if this node is visible
    if (visibleNodeIdsForInventoryFilter.has(node.id)) {
      return true;
    }
    // Check if any children are visible
    return node.children.some(child => 
      nodeOrDescendantsMatchFilters(child, statusFilter, typeFilter, visibleNodeIdsForInventoryFilter)
    );
  }
  
  // No inventory filter - use toolbar filters
  // Check if this node matches
  const nodeMatches = 
    (statusFilter === "All" || node.status.toLowerCase() === statusFilter.toLowerCase()) &&
    (typeFilter === "All" || node.equipmentType === typeFilter);
  
  if (nodeMatches) return true;
  
  // Check if any children match
  return node.children.some(child => 
    nodeOrDescendantsMatchFilters(child, statusFilter, typeFilter, visibleNodeIdsForInventoryFilter)
  );
};

const TreeItem: React.FC<TreeItemProps> = ({
  node,
  level,
  collapsedNodeIds,
  selectedNodeId,
  statusFilter,
  typeFilter,
  hcmLedColor,
  visibleNodeIdsForInventoryFilter,
  onNodeClick,
  onNodeToggle,
}) => {
  const isExpanded = !collapsedNodeIds.has(node.id);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedNodeId === node.id;
  const statusColor = STATUS_COLORS[node.status];
  const nodeColor = NODE_TYPE_CONFIG[node.type]?.color;

  // Extract index from managedObject (e.g., "22-20-49-0201.5.2" -> slot=2)
  let index = '';
  
  // Don't extract index for SITE type (MA-3000) as it already includes the model number
  if (node.managedObject && node.type !== 'site') {
    // Parse managedObject: supports both dot and underscore formats
    // Dot format: chassisSerial.chassisIndex.slot.port.rxu
    // Underscore format (OCH): serialNumber_chassisSerial_chassisIndex_slot_port
    const separator = node.managedObject.includes('_') ? '_' : '.';
    const parts = node.managedObject.split(separator);
    
    if (separator === '_') {
      // Underscore format (OCH): take last 3 numeric parts
      const numericParts = parts.slice(-3);
      if (numericParts.length >= 1 && !isNaN(Number(numericParts[0]))) {
        index = numericParts[0]; // Chassis index for OCH
      }
    } else if (parts.length >= 3) {
      // Dot format
      // For modules: use slot (parts[2])
      // For remotes: use port (parts[3])
      // For RXU: use rxu (parts[4])
      if (parts.length >= 5 && parts[4]) {
        index = parts[4]; // RXU index
      } else if (parts.length >= 4 && parts[3]) {
        index = parts[3]; // Remote port index
      } else if (parts.length >= 3 && parts[2]) {
        index = parts[2]; // Module slot index
      } else if (parts.length >= 2 && parts[1]) {
        index = parts[1]; // Chassis index (HEU, OIU, IHU, OCH)
      }
    }
  }
  
  // Fallback: extract from label if managedObject parsing didn't work
  if (!index && node.type !== 'site') {
    const labelMatch = node.label.match(/[-\s](\d+)$/);
    if (labelMatch) {
      index = labelMatch[1];
    }
  }
  
  // Device name to display outside the chip (hostname or serial number only, empty if neither exists)
  const deviceName = node.metadata?.equipment?.hostname 
    || node.serialNumber 
    || '';

  // Check if node or any descendants match filters
  const shouldShow = nodeOrDescendantsMatchFilters(node, statusFilter, typeFilter, visibleNodeIdsForInventoryFilter);

  // If neither this node nor any descendants match, don't render it
  if (!shouldShow) {
    return null;
  }

  // Check if this specific node matches (for styling purposes)
  const nodeMatches = visibleNodeIdsForInventoryFilter !== null
    ? visibleNodeIdsForInventoryFilter.has(node.id)
    : (statusFilter === "All" || node.status.toLowerCase() === statusFilter.toLowerCase()) &&
      (typeFilter === "All" || node.equipmentType === typeFilter);

  return (
    <>
      <ListItemButton
        onClick={() => onNodeClick(node.id)}
        selected={isSelected}
        sx={{
          pl: 1.5 + level * 2,
          alignItems: "flex-start",
          borderRadius: 1,
          backgroundColor: isSelected ? "rgba(59, 130, 246, 0.1)" : "transparent",
          "&:hover": {
            backgroundColor: isSelected
              ? "rgba(59, 130, 246, 0.15)"
              : "rgba(255, 255, 255, 0.05)",
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", width: "100%" }}>
          {/* Collapse/expand icon */}
          <Box
            onClick={(e) => {
              if (hasChildren) {
                e.stopPropagation();
                onNodeToggle(node.id);
              }
            }}
            sx={{
              width: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: hasChildren ? "pointer" : "default",
            }}
          >
            {hasChildren && (isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
          </Box>

          {/* Status indicator - For MA3000 root, show HCM status; otherwise show device status */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: level === 0 && node.type === 'site' ? hcmLedColor : statusColor,
              boxShadow: level === 0 && node.type === 'site' ? `0 0 8px ${hcmLedColor}` : 'none',
              mt: 0.75,
              flexShrink: 0,
            }}
          />

          {/* Node info - single row format: Type Index Name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            {/* Equipment Type with Index */}
            <Chip
              size="small"
              label={node.label} // Use the label directly from the node
              sx={{
                height: node.label?.startsWith("DEU -") ? 16 : 20,
                minWidth: (() => {
                  if (node.label?.startsWith("DEU -")) return 70;
                  return level === 0 ? 80 : 45; // Wider for top-level root node
                })(),
                fontSize: node.label?.startsWith("DEU -") ? "0.62rem" : "0.7rem",
                fontWeight: 600,
                backgroundColor: nodeColor,
                color: "#fff",
                flexShrink: 0,
              }}
            />

            {/* Device Name - show for OCH from metadata, hide for other chassis nodes (HEU/OIU/IHU) */}
            {(() => {
              // For OCH nodes, show device name from metadata
              if (node.equipmentType === "OCH" && node.metadata?.deviceName) {
                return (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? "#60a5fa" : "#e5e7eb",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.85rem",
                    }}
                  >
                    {node.metadata.deviceName}
                  </Typography>
                );
              }
              // For other nodes (not HEU/OIU/IHU), show the device name
              if (node.equipmentType !== "HEU" && node.equipmentType !== "OIU" && node.equipmentType !== "IHU") {
                return (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? "#60a5fa" : "#e5e7eb",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "0.85rem",
                    }}
                  >
                    {deviceName}
                  </Typography>
                );
              }
              return null;
            })()}
          </Box>
        </Box>
      </ListItemButton>

      {/* Children */}
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List
            disablePadding
            sx={{
              ml: 2.5,
              pl: 1.5,
              borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            {node.children.map((child: TopologyNode) => (
              <TreeItem
                key={child.id}
                node={child}
                level={level + 1}
                collapsedNodeIds={collapsedNodeIds}
                selectedNodeId={selectedNodeId}
                statusFilter={statusFilter}
                typeFilter={typeFilter}
                hcmLedColor={hcmLedColor}
                visibleNodeIdsForInventoryFilter={visibleNodeIdsForInventoryFilter}
                onNodeClick={onNodeClick}
                onNodeToggle={onNodeToggle}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const DasTreeSidebar: React.FC<DasTreeSidebarProps> = ({
  root,
  collapsedNodeIds,
  selectedNodeId,
  isCollapsed,
  statusFilter,
  typeFilter,
  hcmLedColor,
  visibleNodeIdsForInventoryFilter,
  onNodeClick,
  onNodeToggle,
  onSidebarToggle,
}) => {
  if (isCollapsed) {
    return (
      <Box
        sx={{
          width: 40,
          height: "100%",
          backgroundColor: "rgba(15, 23, 42, 0.7)", // slate-900/70 (mockup)
          backdropFilter: "blur(8px)",
          borderRight: "1px solid rgba(255, 255, 255, 0.1)",
          display: "flex",
          alignItems: "flex-start",
          pt: 2,
        }}
      >
        <IconButton onClick={onSidebarToggle} size="small" sx={{ color: "#e5e7eb" }}>
          <ChevronRight />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 320,
        height: "100%",
        backgroundColor: "rgba(15, 23, 42, 0.7)", // slate-900/70 (mockup)
        backdropFilter: "blur(8px)",
        borderRight: "1px solid rgba(255, 255, 255, 0.1)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          backgroundColor: "rgba(15, 23, 42, 0.95)", // slate-900/95 (mockup sticky header)
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Typography variant="h6" sx={{ color: "#e5e7eb", fontWeight: 600 }}>
          Topology Tree
        </Typography>
        <IconButton onClick={onSidebarToggle} size="small" sx={{ color: "#e5e7eb" }}>
          <ChevronLeft />
        </IconButton>
      </Box>

      {/* Tree content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 1 }}>
        {!root && (
          <Typography sx={{ p: 2, color: "#9ca3af" }}>
            No topology data
          </Typography>
        )}
        {root && (
          <List disablePadding>
            <TreeItem
              node={root}
              level={0}
              collapsedNodeIds={collapsedNodeIds}
              selectedNodeId={selectedNodeId}
              statusFilter={statusFilter}
              typeFilter={typeFilter}
              hcmLedColor={hcmLedColor}
              visibleNodeIdsForInventoryFilter={visibleNodeIdsForInventoryFilter}
              onNodeClick={onNodeClick}
              onNodeToggle={onNodeToggle}
            />
          </List>
        )}
      </Box>
    </Box>
  );
};
